// Planilha do Excel (.xlsx) feita no aparelho, sem biblioteca: o .xlsx é um
// .zip com alguns XML (formato SpreadsheetML). Cada aba tem o nome, as
// colunas (título, tipo e largura) e as linhas. A primeira linha fica fixa e
// com filtro; datas e valores vão como números, com o formato brasileiro.
//   tipos: 'texto' · 'data' ('AAAA-MM-DD') · 'dinheiro' · 'numero' · 'inteiro' · 'sim/não'
import { strToU8, zipSync } from 'fflate';

const NS = 'http://schemas.openxmlformats.org/spreadsheetml/2006/main';
const NS_R = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
const XML = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n';

// Estilos (posição em cellXfs): 0 normal · 1 cabeçalho · 2 data · 3 dinheiro · 4 inteiro · 5 número
const ESTILO = { texto: 0, cabecalho: 1, data: 2, dinheiro: 3, inteiro: 4, numero: 5 };

// Caracteres que o XML não aceita (controle, menos tab e quebras) saem.
function escapar(texto) {
  return String(texto)
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\uFFFE\uFFFF]/g, '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// 0 → A, 25 → Z, 26 → AA
export function letraDaColuna(i) {
  let n = i + 1;
  let letras = '';
  while (n > 0) {
    const resto = (n - 1) % 26;
    letras = String.fromCharCode(65 + resto) + letras;
    n = Math.floor((n - 1) / 26);
  }
  return letras;
}

// 'AAAA-MM-DD' → número de série do Excel (dias desde 30/12/1899).
export function serialDaData(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso ?? ''));
  if (!m) return null;
  return Math.round((Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) - Date.UTC(1899, 11, 30)) / 86400000);
}

// O Excel não aceita estes caracteres no nome da aba, nem mais de 31 letras.
function nomeDaAba(nome, usados) {
  let base = String(nome).replace(/[[\]:*?/\\']/g, ' ').trim().slice(0, 31) || 'Aba';
  let final = base;
  for (let i = 2; usados.has(final.toLowerCase()); i++) final = `${base.slice(0, 28)} ${i}`;
  usados.add(final.toLowerCase());
  return final;
}

function celula(ref, valor, tipo) {
  if (valor === null || valor === undefined || valor === '') return '';
  if (tipo === 'data') {
    const serial = serialDaData(valor);
    if (serial !== null) return `<c r="${ref}" s="${ESTILO.data}"><v>${serial}</v></c>`;
  }
  if ((tipo === 'dinheiro' || tipo === 'numero' || tipo === 'inteiro') && Number.isFinite(Number(valor)) && valor !== true && valor !== false) {
    return `<c r="${ref}" s="${ESTILO[tipo]}"><v>${Number(valor)}</v></c>`;
  }
  if (tipo === 'sim/não' || typeof valor === 'boolean') valor = valor ? 'sim' : 'não';
  const texto = escapar(valor);
  const espaco = texto !== texto.trim() || /\n/.test(texto) ? ' xml:space="preserve"' : '';
  return `<c r="${ref}" t="inlineStr"><is><t${espaco}>${texto}</t></is></c>`;
}

function folha(aba) {
  const { colunas, linhas } = aba;
  const ultima = letraDaColuna(Math.max(colunas.length - 1, 0));
  const fim = linhas.length + 1;
  const cols = colunas.map((c, i) => `<col min="${i + 1}" max="${i + 1}" width="${c.largura ?? 14}" customWidth="1"/>`).join('');
  const cabecalho = `<row r="1">${colunas.map((c, i) => `<c r="${letraDaColuna(i)}1" t="inlineStr" s="${ESTILO.cabecalho}"><is><t>${escapar(c.titulo)}</t></is></c>`).join('')}</row>`;
  const corpo = linhas.map((linha, l) => {
    const r = l + 2;
    return `<row r="${r}">${colunas.map((c, i) => celula(`${letraDaColuna(i)}${r}`, linha[i], c.tipo ?? 'texto')).join('')}</row>`;
  }).join('');
  return `${XML}<worksheet xmlns="${NS}" xmlns:r="${NS_R}">`
    + `<dimension ref="A1:${ultima}${fim}"/>`
    + '<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>'
    + '<sheetFormatPr defaultRowHeight="15"/>'
    + (cols ? `<cols>${cols}</cols>` : '')
    + `<sheetData>${cabecalho}${corpo}</sheetData>`
    + (colunas.length ? `<autoFilter ref="A1:${ultima}${fim}"/>` : '')
    + '</worksheet>';
}

const ESTILOS = `${XML}<styleSheet xmlns="${NS}">`
  + '<numFmts count="1"><numFmt numFmtId="164" formatCode="dd/mm/yyyy"/></numFmts>'
  + '<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>'
  + '<fills count="3"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill>'
  + '<fill><patternFill patternType="solid"><fgColor rgb="FFE5EFF9"/><bgColor indexed="64"/></patternFill></fill></fills>'
  + '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>'
  + '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>'
  + '<cellXfs count="6">'
  + '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>'
  + '<xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1"/>'
  + '<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>'
  + '<xf numFmtId="4" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>'
  + '<xf numFmtId="3" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>'
  + '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>'
  + '</cellXfs>'
  + '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>'
  + '</styleSheet>';

// abas: [{ nome, colunas: [{ titulo, tipo, largura }], linhas: [[valor, …]] }] → Uint8Array (.xlsx)
export function planilhaXlsx(abas) {
  const usados = new Set();
  const nomes = abas.map((a) => nomeDaAba(a.nome, usados));
  const arquivos = {};
  arquivos['[Content_Types].xml'] = strToU8(`${XML}<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">`
    + '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>'
    + '<Default Extension="xml" ContentType="application/xml"/>'
    + '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>'
    + '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>'
    + abas.map((_, i) => `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join('')
    + '</Types>');
  arquivos['_rels/.rels'] = strToU8(`${XML}<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">`
    + '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>'
    + '</Relationships>');
  const filtros = abas.map((a, i) => (a.colunas.length
    ? `<definedName name="_xlnm._FilterDatabase" localSheetId="${i}" hidden="1">'${escapar(nomes[i])}'!$A$1:$${letraDaColuna(a.colunas.length - 1)}$${a.linhas.length + 1}</definedName>`
    : '')).join('');
  arquivos['xl/workbook.xml'] = strToU8(`${XML}<workbook xmlns="${NS}" xmlns:r="${NS_R}">`
    + '<bookViews><workbookView/></bookViews>'
    + `<sheets>${nomes.map((n, i) => `<sheet name="${escapar(n)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join('')}</sheets>`
    + (filtros ? `<definedNames>${filtros}</definedNames>` : '')
    + '</workbook>');
  arquivos['xl/_rels/workbook.xml.rels'] = strToU8(`${XML}<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">`
    + abas.map((_, i) => `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`).join('')
    + `<Relationship Id="rId${abas.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>`
    + '</Relationships>');
  arquivos['xl/styles.xml'] = strToU8(ESTILOS);
  abas.forEach((a, i) => {
    arquivos[`xl/worksheets/sheet${i + 1}.xml`] = strToU8(folha(a));
  });
  return zipSync(arquivos, { level: 6 });
}
